import {
    type AutocompleteRenderInputParams,
    type AutocompleteValue,
    Box,
    Chip,
    LinearProgress,
    Autocomplete as MuiAutocomplete,
    type AutocompleteProps as MuiAutocompleteProps,
    type SxProps,
    type Theme,
    Typography,
} from '@mui/material'
import React, { type FC, type ReactElement, type ReactNode, useState } from 'react'

import { copyObjectWithoutKeys, isArray } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, Translation } from '../../../../domain'

import { ErrorMessage } from '../../ErrorMessage'
import { FormattedTranslation } from '../../Internationalization'
import { MenuItem } from '../Dropdown/MenuItem'
import { type DropdownSize, MenuItemClasses } from '../Dropdown/utils'
import { TextField, type TextFieldProps } from './TextField'
import { StatusDot } from '../Status/Dot'

/**
 * Lets any option carry an optional leading icon, picked up by `defaultRenderOption` below when a consumer
 * doesn't supply their own `renderOption`, and unconditionally by `renderMultipleValue`. `icon` being optional
 * means every existing `Value` - objects and primitives alike - already satisfies this structurally, so
 * callers don't need to opt in.
 */
export type WithIcon<Value> = Value & Readonly<{ icon?: ReactElement }>

/** Marks an option as non-removable once selected - `renderMultipleValue` below hides its chip's delete icon */
export type WithRequired<Value> = Value & Readonly<{ required?: boolean }>

/**
 * A right-aligned secondary value, optionally paired with a colored status dot - matches Figma's
 * `LabelMeta` (`meta` alone) and `StatusLabel` (`status` + `meta`) content shapes.
 */
export type WithMeta<Value> = Value & Readonly<{ meta?: ReactNode, status?: 'success' | 'warning' | 'error' }>

/** What the component actually works with internally - stays separately exported since not every consumer needs all three */
type Option<Value> = WithRequired<WithIcon<WithMeta<Value>>>

const hasIcon = <Value,>(value: Value): value is Value & Readonly<{ icon: ReactElement }> =>
    typeof value === 'object' && value !== null && Boolean((value as Readonly<{ icon?: unknown }>).icon)

const hasMeta = <Value,>(value: Value): value is Value & Readonly<{ meta: ReactNode }> =>
    typeof value === 'object' && value !== null && Boolean((value as Readonly<{ meta?: unknown }>).meta)

const hasStatus = <Value,>(value: Value): value is Value & Readonly<{ status: NonNullable<WithMeta<unknown>['status']> }> =>
    typeof value === 'object' && value !== null && Boolean((value as Readonly<{ status?: unknown }>).status)

const isRequired = (value: unknown): boolean => typeof value === 'object' && value !== null && Boolean((value as Readonly<{ required?: unknown }>).required)

type AutocompleteProps<
    Value,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined,
> =
    & Omit<
        MuiAutocompleteProps<Option<Value>, Multiple, DisableClearable, FreeSolo>,
        | 'size'
        | 'filterSelectedOptions'
        | 'noOptionsText'
        | 'onChange'
        | 'onSelect'
        | 'renderOption'
        | 'renderTags'
        | 'renderValue'
        | 'renderInput'
        | 'options'
        | 'inputValue'
        | 'value'
        | 'limitTags'
        | 'getLimitTagsText'
    >
    & Readonly<{
        options: readonly Option<Value>[] | ConnectwareError | null
        noOptionsText: Translation
        onSearch?: (term: string) => void
        onSelect: (value: AutocompleteValue<Option<Value>, Multiple, DisableClearable, FreeSolo>) => void
        /**
         * Readonly, unlike MUI's own `value` - safe since MUI never mutates it in place
         */
        value?: Readonly<AutocompleteValue<Option<Value>, Multiple, DisableClearable, FreeSolo>>
        size?: DropdownSize
    }>

/**
 * MUI's `Autocomplete` only knows the deprecated `InputProps`/`InputLabelProps`/`inputProps` API for its
 * `renderInput` slot - it uses them to wire up the popup/clear adornments, selected-value chips, and the
 * actual input's ref/value/keyboard handlers. This maps those onto the `slotProps` that our TextField accepts,
 * so an Autocomplete stays fully functional without reintroducing the deprecated props on TextField itself.
 * @see https://mui.com/material-ui/api/autocomplete/#autocomplete-prop-renderInput
 */
const adaptAutocompleteRenderInputParams = (params: AutocompleteRenderInputParams): TextFieldProps => ({
    ...copyObjectWithoutKeys(params, 'InputProps', 'InputLabelProps', 'inputProps'),
    slotProps: {
        input: params.InputProps,
        inputLabel: params.InputLabelProps,
        htmlInput: params.inputProps,
    },
})

const MAX_VISIBLE_TAGS = 2

const ICON_DIMENSION: Readonly<Record<DropdownSize, number>> = { small: 14, medium: 16 }
const optionIconStyle = (size: DropdownSize): SxProps<Theme> => {
    const dimension = ICON_DIMENSION[size]
    return { display: 'flex', width: dimension, height: dimension, flexShrink: 0, '& svg': { width: dimension, height: dimension } }
}

/** Figma's status-dot-to-label gap is tighter than the plain icon/label-to-meta gap (12px both sizes) */
const DOT_GAP: Readonly<Record<DropdownSize, number>> = { small: 1.5, medium: 2 } // 6px / 8px
const optionRowStyle = (size: DropdownSize, hasDot: boolean): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: hasDot ? DOT_GAP[size] : 3,
    flexGrow: 1,
    minWidth: 0,
})
const optionLabelStyle: SxProps<Theme> = { flexGrow: 1, minWidth: 0 }
const optionLabelVariant: Readonly<Record<DropdownSize, 'caption' | 'body2'>> = { small: 'caption', medium: 'body2' }

/** `marginLeft: 'auto'` pushes this flush to the row's right edge, independent of whether the label's own `flexGrow` claimed the space first. Figma's trailing text: plain Roboto at a size with no matching theme token */
const META_CAPTION_STYLE: Readonly<Record<DropdownSize, SxProps<Theme>>> = {
    small: { fontSize: '0.6875rem', lineHeight: '0.8125rem' }, // 11px / 13px
    medium: { fontSize: '0.75rem', lineHeight: '0.875rem' }, // 12px / 14px
}
const optionMetaCaptionStyle = (size: DropdownSize): SxProps<Theme> => ({ marginLeft: 'auto', ...META_CAPTION_STYLE[size] })

const DOT_DIMENSION: Readonly<Record<DropdownSize, number>> = { small: 6, medium: 7 }
const getLimitTagsText = (more: number): ReturnType<FC> => <FormattedTranslation id={Translation.MORE_TAGS_COUNT} values={{ count: more }} />

const NoOptionsText: FC<{ translationId: Translation } & Pick<AutocompleteProps<unknown>, 'options'>> = ({ translationId, options }) => {
    return (
        <>
            {isArray(options) && (
                <Typography variant="body2">
                    <FormattedTranslation id={translationId} />
                </Typography>
            )}

            {options === null && (
                <Box>
                    <LinearProgress />
                </Box>
            )}

            {ConnectwareError.is(options) &&
                (ConnectwareError.isOfTypes(options, ConnectwareErrorType.BAD_REQUEST)
                    ? (
                        options.message
                    )
                    : <ErrorMessage titleVariant="body2" error={options} />)}
        </>
    )
}

export const Autocomplete = <
    Value,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined,
>({
    options,
    noOptionsText,
    onSearch,
    onSelect,
    disabled,
    value,
    size = 'medium',
    ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>): ReturnType<FC> => {
    /** `inputValue` isn't an exposed prop (see the Omit above), so this tracks it locally to avoid disabling the field as "no data" mid-typing */
    const [searchTerm, setSearchTerm] = useState('')

    const availableOptions = isArray(options) ? options : []
    /** freeSolo entries aren't picked from `options`, so an empty set doesn't mean there's nothing to do here */
    const noData = !props.freeSolo && !searchTerm && isArray(options) && options.length === 0

    /**
     * `renderOption` isn't a caller-exposed prop (see the Omit above) - every option renders through the
     * shared `Dropdown` menu chrome (`MenuItem`), so consumers express content via `WithIcon`/`WithMeta` data
     * instead of a bespoke render function. One unified row: optional status dot, optional icon, label,
     * optional right-aligned trailing text.
     */
    const defaultRenderOption: NonNullable<MuiAutocompleteProps<Option<Value>, Multiple, DisableClearable, FreeSolo>['renderOption']> = (
        optionProps,
        option,
        state,
        ownerState
    ) => (
        <MenuItem {...optionProps} data-testid={`option-${ownerState.getOptionLabel(option)}`} selected={state.selected} sizeClassName={MenuItemClasses[size]}>
            <Box sx={optionRowStyle(size, hasStatus(option))}>
                {hasStatus(option) && <StatusDot tone={option.status} size={DOT_DIMENSION[size]} />}
                {hasIcon(option) && <Box sx={optionIconStyle(size)}>{option.icon}</Box>}
                <Typography variant={optionLabelVariant[size]} noWrap sx={optionLabelStyle}>
                    {ownerState.getOptionLabel(option)}
                </Typography>
                {hasMeta(option) && (
                    <Typography variant="caption" color="grey.500" sx={optionMetaCaptionStyle(size)}>
                        {option.meta}
                    </Typography>
                )}
            </Box>
        </MenuItem>
    )

    /**
     * Chip equivalent of `defaultRenderOption`, typed against a literal `Multiple = true` - see the cast below.
     * Must return a plain array (not a Fragment) - MUI's own `limitTags`/`getLimitTagsText` truncation only
     * kicks in when `Array.isArray(startAdornment)` holds for whatever this returns.
     */
    const renderMultipleValue: NonNullable<MuiAutocompleteProps<Option<Value>, true, DisableClearable, FreeSolo>['renderValue']> = (
        tags,
        getItemProps,
        ownerState
    ) => tags.map((option, index) => {
        const itemProps = getItemProps({ index })

        return (
            <Chip
                variant="primary"
                size="small"
                icon={hasIcon(option) ? option.icon : undefined}
                label={ownerState.getOptionLabel(option)}
                {...itemProps}
                /** Hides the per-chip delete icon for `WithRequired` options - see `WithRequired` */
                onDelete={isRequired(option) ? undefined : itemProps.onDelete}
                key={index}
            />
        )
    })

    return (
        <MuiAutocomplete<Option<Value>, Multiple, DisableClearable, FreeSolo>
            {...props}
            /**
             * Cast back to mutable for MUI's prop type
             * @see https://github.com/mui/material-ui/pull/47948
             */
            value={value as AutocompleteValue<Option<Value>, Multiple, DisableClearable, FreeSolo>}
            filterSelectedOptions
            size={size}
            limitTags={MAX_VISIBLE_TAGS}
            getLimitTagsText={getLimitTagsText}
            options={availableOptions}
            noOptionsText={<NoOptionsText translationId={noOptionsText} options={options} />}
            disabled={disabled || noData}
            renderOption={defaultRenderOption}
            /**
             * `AutocompleteRenderValue`/`AutocompleteRenderValueGetItemProps` are `Multiple extends true ? ... : ...` conditionals
             * that can't resolve while `Multiple` is still abstract here, so `renderMultipleValue` is typed against a literal
             * `true` instead - the cast below is safe since it's only ever wired in for `multiple` selections
             */
            renderValue={props.multiple
                ? (renderMultipleValue as MuiAutocompleteProps<Option<Value>, Multiple, DisableClearable, FreeSolo>['renderValue'])
                : undefined}
            renderInput={(params) => (
                <TextField
                    {...adaptAutocompleteRenderInputParams(params)}
                    onChange={({ currentTarget: { value } }) => {
                        setSearchTerm(value)
                        onSearch?.(value)
                    }}
                />
            )}
            onChange={(_, value) => onSelect(value)}
        />
    )
}
